// Centralised human-readable labels for user/panel roles.
// Keep in sync with the canonical roles defined in @recruitment/types.

export const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admin',
  system_admin: 'System Admin',
  hcm_admin: 'HCM Admin',
  hcm_reviewer: 'HCM Reviewer',
  assessment_review_approver: 'Assessment Review Approver',
  hcm_support: 'HCM Support',
  qa_qc: 'QA/QC',
  panel_member: 'Panel Member',
  panel_secretary: 'HCM Process Manager',
  panel_lead: 'Panel Lead',
  panel_hcm_rep: 'Panel HCM Rep.',
  panel_sme: 'Panel SME',
  candidate_verifier: 'Candidate Verifier',
  management: 'Management',
  // Legacy aliases
  superadmin: 'Super Admin',
  admin: 'System Admin (Legacy)',
  hr: 'HCM Admin (Legacy)',
  interviewer: 'Panel Member (Legacy)',
  secretary: 'HCM Process Manager (Legacy)',
  screener: 'HCM Admin (Legacy)',
};

/**
 * Initialisms that must stay upper-case. Plain title-casing turns `panel_hcm_rep`
 * into "Panel Hcm Rep", which reads as a typo to anyone in HCM.
 */
const ROLE_ACRONYMS = new Set([
  'hcm',
  'sme',
  'qa',
  'qc',
  'hr',
  'it',
  'sbu',
  'abu',
  'kpi',
  'ai',
  'id',
  'nysc',
]);

/**
 * Returns a human-readable label for a role identifier.
 *
 * Falls back to title-casing the raw role, but keeps known initialisms upper-case —
 * so an unmapped `panel_sme_lead` still reads "Panel SME Lead" rather than
 * "Panel Sme Lead". Use this everywhere a role is shown; several pages used to
 * title-case inline and each produced "Panel Hcm Rep".
 */
export function formatRoleLabel(role?: string | null): string {
  if (!role) return '';
  const known = ROLE_LABELS[role];
  if (known) return known;
  return role
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) =>
      ROLE_ACRONYMS.has(word.toLowerCase())
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(' ');
}

/**
 * Role label for a panel member, defaulting to "Panel Member" when the panel row
 * carries no role. Panel surfaces each had their own inline version of this.
 */
export function formatPanelRoleLabel(role?: string | null): string {
  return role ? formatRoleLabel(role) : 'Panel Member';
}

/**
 * Display order for panel members: the lead first, then the HCM Rep, then SMEs, then
 * everyone else, with the HCM Process Manager (secretary) last since they administer
 * rather than score. Unknown roles sort after the named ones, alphabetically by name.
 *
 * Panel lists were previously in insertion order, so the lead appeared wherever they
 * happened to be added.
 */
const PANEL_ROLE_ORDER: string[] = [
  'panel_lead',
  'panel_hcm_rep',
  'panel_sme',
  'panel_member',
  'panel_secretary',
];

export function panelRolePriority(role?: string | null): number {
  const index = PANEL_ROLE_ORDER.indexOf((role ?? '').toLowerCase());
  // Unknown roles sit between the named scoring roles and the secretary.
  return index === -1 ? PANEL_ROLE_ORDER.length - 1 : index;
}

/**
 * Sorts panel members lead-first. Stable within a role: falls back to the member's
 * display name so the order does not shift between renders.
 */
export function sortPanelMembers<
  T extends { roleName?: string | null; displayName?: string | null; name?: string | null },
>(members: T[]): T[] {
  return [...members].sort((a, b) => {
    const byRole = panelRolePriority(a.roleName) - panelRolePriority(b.roleName);
    if (byRole !== 0) return byRole;
    const aName = a.displayName ?? a.name ?? '';
    const bName = b.displayName ?? b.name ?? '';
    return aName.localeCompare(bName);
  });
}
